/**
 * Hand-mirrored from app/schemas/*.py and app/routes/auth.py.
 * Plan 1 only needs auth + a generic error envelope. Plan 2 adds
 * task / phone-config / status types.
 */

export interface ApiError {
  error: string;
  detail: string;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  username: string;
  expires_in_seconds: number;
}

export interface MeResponse {
  username: string;
  authenticated_via: 'cookie' | 'api_key';
}

export interface PasswordChangeRequest {
  current_password: string;
  new_password: string;
}

export interface PasswordChangeResponse {
  ok: boolean;
}

/* ---------------- CallStatus ---------------- */

export const CALL_STATUSES = [
  'added',
  'downloading',
  'downloaded',
  'download_failed',
  'transcribing',
  'transcribed',
  'transcribe_failed',
  'titling',
  'titled',
  'sending_jira',
  'jira_failed',
  'finished',
  'skipped',
  // Internal "claim" markers set by pipeline workers when they atomically
  // pick up a row. The plain *ing enum values exist in CallStatus but are
  // never set by the backend — workers go from input_status → claiming_<stage>
  // → output_status. The markers are what UI sees as "in flight".
  'claiming_download',
  'claiming_transcribe',
  'claiming_title',
  'claiming_jira',
] as const;

export type CallStatus = (typeof CALL_STATUSES)[number];

export const FAILURE_STATUSES: CallStatus[] = [
  'download_failed',
  'transcribe_failed',
  'jira_failed',
];

export const IN_FLIGHT_STATUSES: CallStatus[] = [
  'claiming_download',
  'claiming_transcribe',
  'claiming_title',
  'claiming_jira',
];

/* ---------------- Tasks ---------------- */

export interface TaskListItem {
  id: number;
  session_id: string;
  status: CallStatus;
  num_a: string | null;
  num_b: string | null;
  call_started_at: string | null;
  duration_seconds: number | null;
  source: string;
  tasks: number;
  jira_issue_key: string | null;
  created_at: string;
}

export interface TaskListResponse {
  items: TaskListItem[];
  next_cursor: string | null;
}

export interface TaskDetail {
  id: number;
  session_id: string;
  status: CallStatus;
  direction: string | null;
  num_a: string | null;
  num_b: string | null;
  external_number: string | null;
  call_started_at: string | null;
  duration_seconds: number | null;
  answer_seconds: number | null;
  source: string;
  source_payload: Record<string, unknown> | null;
  record_url: string | null;
  media_path: string | null;
  tasks: number;
  transcript_payload: Record<string, unknown> | null;
  jira_issue_key: string | null;
  skip_reason: string | null;
  last_error: string | null;
  last_error_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface StatusCounts {
  counts: Partial<Record<CallStatus, number>>;
}

export interface RetryResponse {
  id: number;
  previous_status: CallStatus;
  new_status: CallStatus;
}

export type Stage = 'download' | 'transcribe' | 'title' | 'jira';

export interface RegenerateRequest {
  from_stage?: Stage | null;
}

export interface RegenerateResponse {
  id: number;
  previous_status: CallStatus;
  new_status: CallStatus;
  from_stage: Stage;
  tasks: number;
  instructions: string | null;
  language: string | null;
  lang_candidates: string[] | null;
}

/* ---------------- Tasks list query params (URL ↔ state) ---------------- */

export interface TaskListQuery {
  status?: CallStatus[];
  phone?: string;
  session_id?: string;
  started_from?: string;
  started_to?: string;
  direction?: '1' | '2';
  order?: 'started_desc' | 'started_asc' | 'id_desc' | 'id_asc';
  limit?: number;
  cursor?: string;
}

/* ---------------- Phone config ---------------- */

export interface PhoneConfigItem {
  phone_number: string;
  tasks: number;
  note: string | null;
  instructions: string | null;
  language: string | null;
  lang_candidates: string[] | null;
  created_at: string;
  updated_at: string;
}

export interface PhoneConfigUpsert {
  tasks: number;
  note?: string | null;
  instructions?: string | null;
  language?: string | null;
  lang_candidates?: string[] | null;
}

/* ---------------- Health ---------------- */

export interface PollState {
  last_start_time_seen: string | null;
  last_poll_at: string | null;
}

export interface HealthResponse {
  status: 'ok';
  transcribe_url: string;
  transcribe_reachable: boolean;
  stages_available: string[];
  poll_state: PollState | null;
}

/* ---------------- Poll trigger ---------------- */

export interface PollTriggerResponse {
  fetched: number;
  added: number;
  skipped: number;
  duplicates: number;
}
